import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Form, Input, Row, Col, message, Modal, Space } from "antd";
import { MinusCircleOutlined } from "@ant-design/icons";
import type { PolicyholderVO, InsuredDTO } from "./orders";
import { COMMON_DICTS, getDictLabel, BeneficiaryRelationship, IdType, FamilyMemberRelationship, useMetadataStore, useConfigStore } from "@ifa/utils";
import { CountrySelect } from "@ifa/ui";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import OCRUpload from "../../components/OCRUpload";
import { TagSelector } from "../../components/TagSelector";
import { useFormConfig } from "../../hooks/useFormConfig";
import { IdDocumentInput, type IdDocumentValue } from "../../components/form/IdDocumentInput";
import { BirthDateInput } from "../../components/form/BirthDateInput";
import { PhoneInput } from "../../components/form/PhoneInput";
import { AddressInput } from "../../components/form/AddressInput";
import { SelectInput } from "../../components/form/SelectInput";
import { AmountInput } from "../../components/form/AmountInput";
import {
  parseIdCard,
  getSalutationFromGender,
  resolveBirthDateFromIdDocument,
} from "../../utils/idCardParser";
import {
  getGenderFromSalutation,
  getSalutationFromGenderByContext,
  isZuuTenantContext,
  shouldAutoFillSalutationFromGender,
} from "../../utils/salutationRules";
import {
  isOtherBooleanField,
  normalizeOtherBooleanValue,
  type OtherBooleanField,
} from "./otherBooleanFields";
import styles from "./PersonForm.module.css";

interface PersonFormProps {
  title: string;
  familyMembersSectionTitle?: string;
  data: PolicyholderVO | InsuredDTO;
  onChange: (data: PolicyholderVO | InsuredDTO) => void;
  policyholder?: PolicyholderVO;
  existingPersons?: InsuredDTO[];
  readOnly?: boolean;
  scrollContainer?: HTMLElement | null; // 传入父级滚动容器
  advisorValue?: number;
  advisorOptions?: Array<{ label: string; value: number }>;
  advisorEditable?: boolean;
  advisorLoading?: boolean;
  onAdvisorChange?: (uid: number) => void;
}

const vulnerableCustomerNote =
  "客戶是否無法獨立閱讀、理解及自行簽署保單，需要他人協助辦理投保手續。";

// Helper to check if a section is complete (simplified for now, just basic checks)
// In a real app, this might be more complex or moved to a validation utility

export interface PersonFormRef {
  validate: () => Promise<any>;
}

export const PersonForm = forwardRef<PersonFormRef, PersonFormProps>(({
  title,
  familyMembersSectionTitle,
  data,
  onChange,
  policyholder,
  existingPersons = [],
  readOnly = false,
  scrollContainer,
  advisorValue,
  advisorOptions = [],
  advisorEditable = false,
  advisorLoading = false,
  onAdvisorChange,
}, ref) => {
  const [form] = Form.useForm();
  const brandId = useConfigStore((state) => state.brandId);
  const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
  const isZuuTenant = isZuuTenantContext({ brandId, tenantCode });
  const shouldAutoFillSalutation = shouldAutoFillSalutationFromGender({
    brandId,
    tenantCode,
  });

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const errors = [];

      try {
        const values = await form.validateFields();
        // Merge form values back to data to ensure we have the latest defaults/inputs
        const mergedData = { ...data };

        if (values.basic) mergedData.basic = { ...mergedData.basic, ...values.basic };
        if (values.personal) mergedData.personal = { ...mergedData.personal, ...values.personal };
        if (values.work) mergedData.work = { ...(mergedData as any).work, ...values.work };
        if (values.tax) mergedData.tax = { ...mergedData.tax, ...values.tax };

        // Handle other fields (new structure)
        if (!mergedData.other) mergedData.other = {};
        if (values.isVulnerableCustomer !== undefined) mergedData.other.isVulnerableCustomer = normalizeOtherBooleanValue(values.isVulnerableCustomer);
        if (values.isProfessionalInvestor !== undefined) mergedData.other.isProfessionalInvestor = normalizeOtherBooleanValue(values.isProfessionalInvestor);
        if (values.remark !== undefined) mergedData.other.remark = values.remark;
        if (values.witnessName !== undefined) mergedData.other.witnessName = values.witnessName;
        if (values.witnessIdDocument?.idType !== undefined) mergedData.other.witnessIdType = values.witnessIdDocument.idType;
        if (values.witnessIdDocument?.idNumber !== undefined) mergedData.other.witnessIdNumber = values.witnessIdDocument.idNumber;

        // Ensure defaults for boolean fields
        if (mergedData.personal?.smoke == null) mergedData.personal = { ...mergedData.personal, smoke: null };

        if (!mergedData.tax) mergedData.tax = {};
        if (mergedData.tax.hasOtherTaxResidency == null) mergedData.tax.hasOtherTaxResidency = null;
        if (mergedData.tax.isUSCitizen == null) mergedData.tax.isUSCitizen = null;
        if (mergedData.tax.hasHKIDCard == null) mergedData.tax.hasHKIDCard = null;
        if (mergedData.tax.isHKPermanentResident == null) mergedData.tax.isHKPermanentResident = null;

        // Ensure defaults for other fields
        if (!mergedData.other) mergedData.other = {};
        if (!isZuuTenant) {
          if (mergedData.other.isVulnerableCustomer == null) mergedData.other.isVulnerableCustomer = false;
          if (mergedData.other.isProfessionalInvestor == null) mergedData.other.isProfessionalInvestor = false;
        }

        onChange(mergedData);
      } catch (error: any) {
        if (error.errorFields) {
          errors.push(...error.errorFields);
        }
      }

      if (errors.length > 0) {
        // 滚动到第一个错误字段
        setTimeout(() => {
          if (scrollContainer) {
            const errorFormItem = document.querySelector(".ant-form-item-has-error");

            if (errorFormItem) {
              const elementRect = errorFormItem.getBoundingClientRect();
              const containerRect = scrollContainer.getBoundingClientRect();
              const scrollTop = scrollContainer.scrollTop;
              const targetScrollTop = scrollTop + elementRect.top - containerRect.top - 100;

              scrollContainer.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: "smooth",
              });
            }
          }
        }, 100);

        throw { errorFields: errors };
      }
    },
  }));

  const [selectModalVisible, setSelectModalVisible] = useState(false);
  const { selectMetadataList, formRequiredConfig } = useMetadataStore();

  // Track if initial defaults have been set to avoid repeated updates
  const initialDefaultsSetRef = React.useRef(false);
  const prevTitleRef = React.useRef(title);

  /**
   * 獲取選項列表
   * 優先從元數據獲取，若無則使用後備列表
   * @param code 元數據代碼
   * @param fallback 後備選項列表
   */
  const getOptions = (code: string, fallback: any[] = []) => {
    const meta = selectMetadataList.find((m) => m.code === code);
    if (meta?.items?.length) {
      return meta.items.map((i) => ({ label: i.name || i.key, value: i.key }));
    }
    return fallback;
  };

  const relationshipOptions = getOptions("RELATIONSHIP", COMMON_DICTS.relationship);
  const whetherOptions = getOptions("WHETHER", COMMON_DICTS.boolean);
  const otherBooleanOptions = COMMON_DICTS.boolean;
  const smokeOptions = getOptions("SMOKE", COMMON_DICTS.boolean);
  const isPolicyholder = title === "投保人";
  const getOtherBooleanValue = (field: OtherBooleanField) =>
    normalizeOtherBooleanValue(data.other?.[field] ?? (data as any)[field]);
  // Initialize form values and ensure defaults in data
  useEffect(() => {
    // 重要：使用 resetFields + setFieldsValue 确保切换页面时清空旧数据
    // 只有在切换不同的人员表单时才重置字段 (根据 title 判断)
    if (prevTitleRef.current !== title) {
      form.resetFields();
      initialDefaultsSetRef.current = false;
      prevTitleRef.current = title;
    }

    // 根据身份证类型自动设置 hasHKIDCard（仅在为空时）
    let autoHasHKIDCard = data.tax?.hasHKIDCard;
    if (autoHasHKIDCard === null || autoHasHKIDCard === undefined) {
      if (data.basic?.idType === IdType.HongKongID) {
        autoHasHKIDCard = "YES";
      } else if (data.basic?.idType) {
        // 其他证件类型默认为 NO
        autoHasHKIDCard = "NO";
      }
    }

    form.setFieldsValue({
      basic: {
        ...data.basic,
        salutation: data.basic?.salutation, // 使用 basic.salutation 作为表单字段值
        idDocument: {
          idType: data.basic?.idType,
          idNumber: data.basic?.idNumber,
        },
      },
      personal: {
        ...{
          ...data.personal,
          smoke: data.personal?.smoke ?? false,
          addressData: {
            country: (data.personal as any)?.country,
            address: (data.personal as any)?.address,
          },
        },
        smoke: data.personal?.smoke ?? false,
      },
      work: (data as PolicyholderVO).work,
      tax: {
        ...{
          ...data.tax,
          hasOtherTaxResidency: data.tax?.hasOtherTaxResidency ?? null,
          isUSCitizen: data.tax?.isUSCitizen ?? null,
          hasHKIDCard: autoHasHKIDCard ?? null,
          isHKPermanentResident: data.tax?.isHKPermanentResident ?? null,
        },
        hasOtherTaxResidency: data.tax?.hasOtherTaxResidency ?? null,
        isUSCitizen: data.tax?.isUSCitizen ?? null,
        hasHKIDCard: autoHasHKIDCard ?? null,
        isHKPermanentResident: data.tax?.isHKPermanentResident ?? null,
      },
      familyMembers: (data as PolicyholderVO).familyMembers ?? null,
      relationship: (data as PolicyholderVO).relationship ?? null,
      isVulnerableCustomer: data.other?.isVulnerableCustomer ?? null,
      isProfessionalInvestor: data.other?.isProfessionalInvestor ?? null,
      remark: data.other?.remark ?? data.remark,
      witnessName: data.other?.witnessName,
      witnessIdDocument: {
        idType: data.other?.witnessIdType,
        idNumber: data.other?.witnessIdNumber,
      },
    });

    // Ensure data has defaults for saving - Only run once per data object to prevent infinite loops
    if (!readOnly && !initialDefaultsSetRef.current) {
      let shouldUpdate = false;
      // Deep clone to avoid mutating shared references between policyholder and insured
      let newData = {
        ...data,
        basic: data.basic ? { ...data.basic } : undefined,
        personal: data.personal ? { ...data.personal } : {},
        tax: data.tax ? { ...data.tax } : undefined,
        work: (data as PolicyholderVO).work ? { ...(data as PolicyholderVO).work } : undefined,
      };

      const hasOriginalProfessionalInvestorValue =
        data.other?.isProfessionalInvestor != null ||
        (data as any).isProfessionalInvestor != null;

      // 首先应用 formRequiredConfig 中的默认值
      const dataWithConfigDefaults = applyDefaultValues(newData);
      if (dataWithConfigDefaults !== newData) {
        newData = dataWithConfigDefaults;
        shouldUpdate = true; // 修正：应用了默认值，需要更新
      }

      if (isZuuTenant && !hasOriginalProfessionalInvestorValue) {
        if (newData.other && "isProfessionalInvestor" in newData.other) {
          delete newData.other.isProfessionalInvestor;
        }
        if ("isProfessionalInvestor" in newData) {
          delete newData.isProfessionalInvestor;
        }
      }

      // 根据身份证类型自动设置 hasHKIDCard（仅在为空时）
      if (!newData.tax) newData.tax = {};
      if (newData.tax.hasHKIDCard === null || newData.tax.hasHKIDCard === undefined) {
        if (newData.basic?.idType === IdType.HongKongID) {
          newData.tax.hasHKIDCard = "YES";
          shouldUpdate = true;
        } else if (newData.basic?.idType) {
          // 其他证件类型默认为 NO
          newData.tax.hasHKIDCard = "NO";
          shouldUpdate = true;
        }
      }

      // 根据性别自动填充称谓（仅在称谓为空时）- 确保 personal.salutation 有值
      if (!newData.personal) newData.personal = {};
      if (
        !newData.personal.salutation &&
        newData.basic?.gender &&
        shouldAutoFillSalutation
      ) {
        console.log(`PersonForm 初始化: 根据性别 ${newData.basic.gender} 自动填充称谓到 personal.salutation`);
        newData.personal.salutation = newData.basic.gender;
        shouldUpdate = true;
      }

      // Other fields - initialize other object
      if (!newData.other) newData.other = {} as any;
      if (!isZuuTenant) {
        if (newData.other.isVulnerableCustomer === undefined || newData.other.isVulnerableCustomer === null) {
          newData.other.isVulnerableCustomer = false;
          shouldUpdate = true;
        }
        if (newData.other.isProfessionalInvestor === undefined || newData.other.isProfessionalInvestor === null) {
          newData.other.isProfessionalInvestor = false;
          shouldUpdate = true;
        }
      }

      if (shouldUpdate) {
        console.log(`PersonForm 初始化完成，personal.salutation =`, newData.personal?.salutation);
        onChange(newData);
        // Mark that defaults have been set for this data object
        initialDefaultsSetRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, form, isZuuTenant, readOnly, title]);

  // 监听性别变化，自动同步称谓
  useEffect(() => {
    if (data.basic?.gender && data.personal && !isZuuTenant) {
      const salutation = getSalutationFromGender(data.basic.gender);

      // 只在称谓为空或与性别不一致时更新
      if (salutation && (!data.personal.salutation || data.personal.salutation !== salutation)) {
        const newData = {
          ...data,
          basic: {
            ...data.basic,
            salutation, // 同步更新 basic.salutation，确保表单显示正确
          },
          personal: {
            ...data.personal,
            salutation,
          },
        };
        onChange(newData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.basic?.gender, data.personal, isZuuTenant]); // 只监听性别变化，避免无限循环

  /**
   * 更新表單數據
   * 根據 section 和 field 更新對應的數據結構
   * @param section 數據區塊 (basic, personal, work, tax, other)
   * @param field 字段名
   * @param value 新值
   */
  const updateData = (section: string, field: string, value: any) => {
    // 记录具体字段更新
    console.log(`PersonForm 字段更新: ${section}.${field}`, "新值:", value);
    const nextValue =
      section === "other" && isOtherBooleanField(field)
        ? normalizeOtherBooleanValue(value)
        : value;

    // Deep clone to prevent shared references
    const newData = {
      ...data,
      basic: data.basic ? { ...data.basic } : undefined,
      personal: data.personal ? { ...data.personal } : undefined,
      work: (data as PolicyholderVO).work
        ? {
            ...(data as PolicyholderVO).work,
            employer: (data as PolicyholderVO).work?.employer ? { ...(data as PolicyholderVO).work.employer } : undefined,
          }
        : undefined,
      tax: data.tax ? { ...data.tax } : undefined,
    };

    if (section === "familyMembers") {
      // Handled separately
    } else if (section === "other") {
      (newData as any).other = { ...(newData as any).other, [field]: nextValue };
    } else {
      // Deep merge for basic, personal, work, tax
      (newData as any)[section] = {
        ...(newData as any)[section],
        [field]: nextValue,
      };
    }
    onChange(newData);
  };

  /**
   * 根据证件类型获取默认的国家/地区信息
   * @param idType 证件类型
   * @returns 默认的国家代码和区号
   */
  const getDefaultsByIdType = (idType?: string) => {
    switch (idType) {
      case IdType.HongkongID:
        return {
          countryCode: "HONGKONG", // 香港
          phoneCode: "852",
        };
      case IdType.IDCard:
        return {
          countryCode: "CHINESE", // 中国
          phoneCode: "86",
        };
      case IdType.MacaoID:
        return {
          countryCode: "MACAO", // 澳门
          phoneCode: "853",
        };
      case IdType.TaiwanID:
        return {
          countryCode: "TAIWAN", // 台湾
          phoneCode: "886",
        };
      default:
        return {
          countryCode: undefined,
          phoneCode: undefined,
        };
    }
  };

  /**
   * 處理 OCR 識別成功後的回調
   * 將識別結果填入表單
   * @param res OCR 識別結果
   */
  const handleOCRSuccess = (res: any) => {
    // 根據性別自動設置稱謂（只在稱謂為空時才自動填充）
    let salutation = data.basic?.salutation;
    if (!salutation) {
      // 只有當稱謂為空時才自動填充
      if (res.gender === "MALE") {
        salutation = "MALE"; // MR先生
      } else if (res.gender === "FEMALE") {
        salutation = "FEMALE"; // MS女士
      }
    }

    // 根据证件类型获取默认值
    const defaults = getDefaultsByIdType(res.type);

    const newData = {
      ...data,
      basic: {
        ...data.basic,
        chineseSurname: res.lastnameCn,
        chineseGivenName: res.firstnameCn,
        // 英文名字自动转换为大写
        englishSurname: res.lastnameEn?.toUpperCase(),
        englishGivenName: res.firstnameEn?.toUpperCase(),
        birthDate: res.birthDate,
        idNumber: res.idNumber,
        gender: res.gender,
        salutation, // 自動設置稱謂
        idType: res.type,
      },
      personal: {
        ...data.personal,
        salutation, // 同步更新 personal.salutation，确保保存时有值
        // 手机区号（仅在为空时）
        phone: !data.personal?.phone && defaults.phoneCode ? `${defaults.phoneCode} ` : data.personal?.phone,
        // 通讯地址国家（仅在为空时）
        ...(!(data.personal as any)?.country && defaults.countryCode ? { country: defaults.countryCode } : {}),
      },
      tax: {
        ...data.tax,
        // 出生国家（仅在为空时）
        countryOfBirth: !data.tax?.countryOfBirth && defaults.countryCode ? defaults.countryCode : data.tax?.countryOfBirth,
        // 国籍（仅在为空时）
        nationality: !data.tax?.nationality && defaults.countryCode ? defaults.countryCode : data.tax?.nationality,
        // 税务国家（仅在为空时）
        ...(!(data.tax as any)?.taxCountry && defaults.countryCode ? { taxCountry: defaults.countryCode } : {}),
      },
    };
    onChange(newData);

    // 更新表單的性別和稱謂欄位
    form.setFieldsValue({
      basic: {
        gender: res.gender,
        salutation: salutation,
      },
      personal: {
        phone: newData.personal?.phone,
        addressData: {
          country: (newData.personal as any)?.country,
          address: (newData.personal as any)?.address,
        },
      },
      tax: {
        countryOfBirth: newData.tax?.countryOfBirth,
        countryOfResidence: newData.tax?.countryOfResidence,
        nationality: newData.tax?.nationality,
      },
    });
  };

  const handleSelectExisting = (person: InsuredDTO) => {
    const newData = {
      ...data,
      basic: { ...data.basic, ...person.basic },
      personal: { ...data.personal, ...person.personal },
      tax: { ...data.tax, ...person.tax },
      // Don't copy ID to avoid conflict with order-specific IDs
    };
    onChange(newData);
    setSelectModalVisible(false);
    message.success("已填入受保人資料");
  };

  const isSecondPolicyholder = title.includes("第二投保人");
  const isSecondInsured = title.includes("第二受保人");
  const isSimplifiedForm = isSecondPolicyholder || isSecondInsured;
  const isInsured = title.includes("受保人");
  const isPrimaryInsured = isInsured && !isSecondInsured; // 主受保人（非第二受保人）
  const insuredRelationship = (data as PolicyholderVO).relationship;
  const canUsePolicyholderQuickFill =
    isInsured &&
    !readOnly &&
    !!policyholder &&
    !!insuredRelationship &&
    insuredRelationship !== BeneficiaryRelationship.Self;

  const quickActionStyle: React.CSSProperties = {
    color: "var(--theme-primary)  ",
    cursor: "pointer",
    fontSize: 14,
    whiteSpace: "nowrap",
  };
  const getSectionNumber = (baseNumber: number) => {
    if (isInsured || isSecondPolicyholder) return String(baseNumber + 1).padStart(2, "0");
    return String(baseNumber).padStart(2, "0");
  };

  const syncPersonalFormValues = (personal: any) => {
    form.setFieldsValue({
      personal: {
        ...personal,
        addressData: {
          country: personal?.country,
          address: personal?.address,
        },
      },
    });
  };

  const updatePersonalData = (updater: (personal: any) => any) => {
    const newData = { ...data };
    const nextPersonal = updater({ ...(newData.personal || {}) });
    newData.personal = nextPersonal;
    syncPersonalFormValues(nextPersonal);
    onChange(newData);
  };

  const handleCopyPolicyholderPhone = () => {
    const policyholderPhone = policyholder?.personal?.phone;
    if (!policyholderPhone) {
      message.warning("投保人手提電話為空，無法填充");
      return;
    }

    updatePersonalData((personal) => ({
      ...personal,
      phone: policyholderPhone,
    }));
  };

  const handleCopyPolicyholderEmail = () => {
    const policyholderEmail = policyholder?.personal?.email;
    if (!policyholderEmail) {
      message.warning("投保人電郵為空，無法填充");
      return;
    }

    updatePersonalData((personal) => ({
      ...personal,
      email: policyholderEmail,
    }));
  };

  const handleCopyPolicyholderCommunicationAddress = () => {
    const policyholderCountry = (policyholder?.personal as any)?.country;
    const policyholderAddress = (policyholder?.personal as any)?.address;

    if (!policyholderCountry && !policyholderAddress) {
      message.warning("投保人通訊地址為空，無法填充");
      return;
    }

    updatePersonalData((personal) => ({
      ...personal,
      country: policyholderCountry,
      address: policyholderAddress,
    }));
  };

  const handleCopyPolicyholderResidentialAddress = () => {
    const residentialAddress = (policyholder?.personal as any)?.residentialAddress;
    const residentialAddressRegion = (policyholder?.personal as any)?.residentialAddressRegion;

    if (!residentialAddress) {
      message.warning("投保人住宅(身份證)地址為空，無法填充");
      return;
    }

    updatePersonalData((personal) => ({
      ...personal,
      country: residentialAddressRegion || personal?.country,
      address: residentialAddress,
    }));
  };

  // Styles to match CustomerSelectModal (consistent with BeneficiaryForm)
  const cardStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: 12,
    padding: "16px 24px",
    marginBottom: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.3s",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.02)",
    cursor: "pointer",
  };

  const nameStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    color: "#333",
    marginRight: 12,
  };

  const tagStyle: React.CSSProperties = {
    backgroundColor: "#fff7e6",
    color: "#fa8c16",
    border: "1px solid #ffd591",
    fontSize: 12,
    padding: "1px 8px",
    borderRadius: 4,
    lineHeight: 1.5,
  };

  const detailStyle: React.CSSProperties = {
    fontSize: 13,
    color: "#999",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 8,
  };

  // Prepare options for Selects
  const genderOptions = getOptions("GENDER", COMMON_DICTS.gender);
  const salutationOptions = getOptions("SALUTATION", COMMON_DICTS.salutation);
  const educationOptions = getOptions("EDUCATION", COMMON_DICTS.education);
  const marriageOptions = getOptions("MARRIAGE_STATUS", COMMON_DICTS.maritalStatus);
  const jobTypeOptions = getOptions("WORK_INFO_EMPLOYMENT_TYPE", COMMON_DICTS.jobType);
  const industryOptions = getOptions("WORK_INFO_EMPLOYER_INDUSTRY", []);
  const retirementAgeOptions = getOptions("INTENDED_RETIREMENT_AGE_OPTION", []);
  const hkEntryIdTypeOptions = getOptions("HK_ENTRY_ID_TYPE", COMMON_DICTS.hkEntryIdType);
  const familyRelationshipOptions = getOptions("FAMILY_MEMBER_RELATIONSHIP", COMMON_DICTS.familyRelationShip);

  // Determine form key for validation config
  let formKey = "";
  if (title.includes("第二投保人")) {
    formKey = "SecondPolicyholderForm";
  } else if (title.includes("第二受保人")) {
    formKey = "SecondInsuredForm";
  } else if (title.includes("受保人")) {
    formKey = "InsuredForm";
  } else if (title.includes("投保人")) {
    formKey = "PolicyholderForm";
  }

  const { isRequired, isVisible, applyDefaultValues } = useFormConfig(formKey, formRequiredConfig, data);
  const resolvedFamilyMembersSectionTitle =
    familyMembersSectionTitle ?? `${title}家庭成員`;

  // 性别选择器包装组件，处理性别到称谓的联动
  const GenderSelector: React.FC<{ value?: any; onChange?: (value: any) => void; disabled?: boolean }> = ({ value, onChange: formOnChange, disabled }) => {
    return (
      <TagSelector
        options={genderOptions}
        value={value}
        onChange={(v) => {
          // 先调用 Form.Item 注入的 onChange
          formOnChange?.(v);

          // 然后处理联动逻辑：性别变更时，自动更新称谓
          const currentSalutation = form.getFieldValue(["basic", "salutation"]);
          let autoSalutation = currentSalutation;

          if (currentSalutation !== "UNIDENTIFIED") {
            autoSalutation = getSalutationFromGenderByContext(v, salutationOptions, {
              brandId,
              tenantCode,
            }) ?? currentSalutation;
          }

          // 更新称谓字段
          if (autoSalutation && autoSalutation !== currentSalutation) {
            form.setFieldsValue({
              basic: {
                salutation: autoSalutation,
              },
            });

            // 更新 data 对象
            const newData = {
              ...data,
              basic: {
                ...data.basic,
                gender: v,
                salutation: autoSalutation,
              },
              personal: {
                ...data.personal,
                salutation: autoSalutation,
              },
            };
            onChange(newData);
          } else {
            // 只更新性别
            const newData = {
              ...data,
              basic: {
                ...data.basic,
                gender: v,
              },
            };
            onChange(newData);
          }
        }}
        disabled={disabled}
        allowClear={false}
      />
    );
  };

  // 称谓选择器包装组件，处理称谓到性别的联动
  const SalutationSelector: React.FC<{ value?: any; onChange?: (value: any) => void; disabled?: boolean }> = ({ value, onChange: formOnChange, disabled }) => {
    return (
      <TagSelector
        options={salutationOptions}
        value={value}
        onChange={(v) => {
          // 先调用 Form.Item 注入的 onChange
          formOnChange?.(v);

          // 然后处理联动逻辑：称谓变更时，自动更新性别
          const currentGender = form.getFieldValue(["basic", "gender"]);
          const autoGender = getGenderFromSalutation(v, { brandId, tenantCode }) ?? currentGender;

          // 更新性别字段
          if (autoGender && autoGender !== currentGender) {
            form.setFieldsValue({
              basic: {
                gender: autoGender,
              },
            });

            // 更新 data 对象
            const newData = {
              ...data,
              basic: {
                ...data.basic,
                salutation: v,
                gender: autoGender,
              },
              personal: {
                ...data.personal,
                salutation: v,
              },
            };
            onChange(newData);
          } else {
            // 只更新称谓
            const newData = {
              ...data,
              basic: {
                ...data.basic,
                salutation: v,
              },
              personal: {
                ...data.personal,
                salutation: v,
              },
            };
            onChange(newData);
          }
        }}
        disabled={disabled}
        allowClear={false}
      />
    );
  };

  return (
    <div className={`person-form ${styles.personForm}`}>
      <Modal title="選擇現有受保人" open={selectModalVisible} onCancel={() => setSelectModalVisible(false)} footer={null} width={600}>
        <div style={{ maxHeight: 400, overflowY: "auto", padding: 4 }}>
          {existingPersons.map((p) => (
            <div
              key={p.insuredId}
              onClick={() => handleSelectExisting(p)}
              style={cardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--theme-primary)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e8e8e8";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.02)";
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={nameStyle}>
                    {p.basic?.chineseSurname}
                    {p.basic?.chineseGivenName} ({p.basic?.englishSurname} {p.basic?.englishGivenName})
                  </span>
                  <span style={tagStyle}>{getDictLabel(COMMON_DICTS.gender, p.basic?.gender)}</span>
                </div>
                <div style={detailStyle}>
                  <span style={{ marginRight: 16, position: "relative" }}>
                    {p.basic?.idNumber}
                    <span
                      style={{
                        position: "absolute",
                        right: -9,
                        top: 0,
                        color: "#e8e8e8",
                      }}
                    >
                      |
                    </span>
                  </span>
                </div>
              </div>
              <CustomButton
                height={32}
                bgColor="var(--theme-primary)"
                color="#fff"
                onClick={(e) => {
                  e.stopPropagation(); // 阻止事件冒泡
                  handleSelectExisting(p);
                }}
              >
                選擇
              </CustomButton>
            </div>
          ))}
          {existingPersons.length === 0 && <div className="text-center py-8 text-gray-400">暫無可選的現有受保人</div>}
        </div>
      </Modal>
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        disabled={readOnly}
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        }}
        onFinishFailed={(errorInfo) => {
          if (scrollContainer && errorInfo.errorFields.length > 0) {
            setTimeout(() => {
              const hasError = document.querySelector(".ant-form-item-has-error");
              if (hasError) {
                const elementRect = hasError.getBoundingClientRect();
                const containerRect = scrollContainer.getBoundingClientRect();
                const scrollTop = scrollContainer.scrollTop;
                const targetScrollTop = scrollTop + elementRect.top - containerRect.top - 100;

                scrollContainer.scrollTo({
                  top: Math.max(0, targetScrollTop),
                  behavior: "smooth",
                });
              }
            }, 150);
          }
        }}
      >
        {/* 01 Relationship (Only for Insured Person or Second Policyholder) */}
        {(isInsured || isSecondPolicyholder) && isVisible("relationship") && (
          <>
            <CustomCard
              title={isSecondPolicyholder ? "01 與受保人關係" : "01 與投保人關係"}
              className="mb-4"
              bordered={false}
              bodyStyle={{ display: "none" }}
              extra={
                <div style={{ width: 346 }}>
                  <Form.Item name="relationship" rules={[{ required: isRequired("relationship"), message: "請選擇關係" }]} style={{ marginBottom: 0 }}>
                    <SelectInput
                      options={relationshipOptions}
                      value={(data as PolicyholderVO).relationship}
                      onChange={(v) => onChange({ ...data, relationship: v } as any)}
                      title={isSecondPolicyholder ? "與受保人關係" : "與投保人關係"}
                      readOnly={readOnly}
                    />
                  </Form.Item>
                </div>
              }
            />
          </>
        )}

        {/* Basic Info */}
        {(isInsured || isSecondPolicyholder) && (data as PolicyholderVO).relationship === BeneficiaryRelationship.Self ? (
          <div
            style={{
              padding: "12px 24px",
              background: "#f5f5f5",
              borderRadius: 8,
              marginBottom: 24,
              color: "#999",
              textAlign: "center",
            }}
          >
            {isSecondPolicyholder
              ? "第二投保人與受保人關係為本人，詳細資料將自動與受保人保持一致，無需展示。"
              : isInsured
                ? "受保人與投保人關係為本人，詳細資料將自動與受保人保持一致，無需展示。"
                : ""}
          </div>
        ) : (isInsured || isSecondPolicyholder) && !(data as PolicyholderVO).relationship ? null : (
          <>
            {isPolicyholder && (
              <CustomCard
                title="顧問"
                className="mb-4"
                bordered={false}
                bodyStyle={{ display: "none" }}
                extra={
                  <div style={{ width: 346 }}>
                    <SelectInput
                      options={advisorOptions}
                      value={advisorValue}
                      onChange={onAdvisorChange}
                      placeholder={advisorLoading ? "加載中..." : "請選擇"}
                      title="選擇顧問"
                      readOnly={!advisorEditable}
                    />
                  </div>
                }
              />
            )}
            <CustomCard
              title={`${getSectionNumber(1)} ${title}基本資料`}
              className="mb-4"
              bordered={false}
              extra={
                <Space>
                  {!readOnly && <OCRUpload onSuccess={handleOCRSuccess} idType={data.basic?.idType} />}
                  {isInsured && !readOnly && (
                    <CustomButton height={32} bgColor="#fff" color="var(--theme-primary)" style={{ border: "1px solid var(--theme-primary)" }} onClick={() => setSelectModalVisible(true)}>
                      選擇現有受保人
                    </CustomButton>
                  )}
                </Space>
              }
            >
              <Row gutter={24}>
                {isVisible("basic.idNumber") && (
                  <Col span={24}>
                    <Form.Item
                      label="身份證明文件號碼"
                      layout="horizontal"
                      labelAlign="left"
                      labelCol={{ span: 12 }}
                      wrapperCol={{ span: 12 }}
                      name={["basic", "idDocument"]}
                      rules={[
                        {
                          validator: async (_, value) => {
                            if (isRequired("basic.idNumber")) {
                              if (!value) {
                                return Promise.reject(new Error("請完善身份證明文件資料"));
                              }
                              if (!value.idType) {
                                return Promise.reject(new Error("請選擇證件類型"));
                              }
                              if (!value.idNumber) {
                                return Promise.reject(new Error("請輸入身份證明文件號碼"));
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <div style={{ paddingLeft: "12px" }}>
                        <IdDocumentInput
                          value={{
                            idType: data.basic?.idType,
                            idNumber: data.basic?.idNumber,
                          }}
                          onChange={(value: IdDocumentValue) => {
                            console.log("=== IdDocumentInput onChange 触发 ===");
                            console.log("输入的证件类型:", value.idType);
                            console.log("输入的证件号码:", value.idNumber);
                            console.log("当前 data.basic?.birthDate:", data.basic?.birthDate);
                            console.log("当前 data.basic?.gender:", data.basic?.gender);
                            console.log("当前 data.tax?.hasHKIDCard:", data.tax?.hasHKIDCard);

                            const newData = { ...data };
                            if (!newData.basic) newData.basic = {};
                            if (!newData.personal) newData.personal = {};
                            if (!newData.tax) newData.tax = {};

                            // 更新证件类型和号码
                            newData.basic.idType = value.idType;
                            newData.basic.idNumber = value.idNumber;

                            // 根据身份证号自动填充出生日期和性别
                            if (value.idNumber) {
                              const parsedResult = parseIdCard(value.idNumber, value.idType);
                              console.log("解析结果:", parsedResult);

                              if (parsedResult.success) {
                                const syncedBirthDate = resolveBirthDateFromIdDocument(
                                  value.idType,
                                  newData.basic.birthDate,
                                  parsedResult.birthDate,
                                );

                                if (syncedBirthDate) {
                                  console.log("✅ 自动同步出生日期:", syncedBirthDate);
                                  newData.basic.birthDate = syncedBirthDate;
                                } else {
                                  console.log("❌ 不填充出生日期 - parsedResult.birthDate:", parsedResult.birthDate, "newData.basic.birthDate:", newData.basic.birthDate);
                                }

                                // 仅在性别为空时填充
                                if (parsedResult.gender && !newData.basic.gender) {
                                  console.log("✅ 自动填充性别:", parsedResult.gender);
                                  newData.basic.gender = parsedResult.gender;
                                } else {
                                  console.log("❌ 不填充性别 - parsedResult.gender:", parsedResult.gender, "newData.basic.gender:", newData.basic.gender);
                                }

                                // 自动填充称谓（独立判断，仅在称谓为空时填充）
                                if (
                                  parsedResult.gender &&
                                  shouldAutoFillSalutation
                                ) {
                                  const salutation = getSalutationFromGender(parsedResult.gender);
                                  if (salutation && !newData.personal.salutation) {
                                    console.log("✅ 自动填充称谓:", salutation);
                                    newData.personal.salutation = salutation;
                                  } else {
                                    console.log("❌ 不填充称谓 - salutation:", salutation, "newData.personal.salutation:", newData.personal.salutation);
                                  }
                                }
                              }
                            }

                            // 根据证件类型自动设置"是否持有香港身份证"（无论是否切换类型）
                            console.log("检查证件类型 - value.idType:", value.idType);
                            console.log("IdType.HongkongID 枚举值:", IdType.HongkongID);
                            console.log("比较结果:", value.idType === IdType.HongkongID);
                            console.log("字符串比较:", value.idType === "HONGKONG_ID");

                            // 支持多种香港身份证的枚举值格式
                            const isHongKongId = value.idType === IdType.HongkongID || value.idType === "HONGKONG_ID" || value.idType === "HK_ID";

                            if (isHongKongId) {
                              console.log("✅ 设置 hasHKIDCard = 'YES' (香港身份证)");
                              newData.tax.hasHKIDCard = "YES";
                            } else {
                              console.log("✅ 设置 hasHKIDCard = 'NO' (非香港身份证)");
                              newData.tax.hasHKIDCard = "NO";
                            }

                            console.log("newData.tax.hasHKIDCard 最终值:", newData.tax.hasHKIDCard);
                            console.log("newData.tax.hasHKIDCard 类型:", typeof newData.tax.hasHKIDCard);
                            console.log("whetherOptions:", whetherOptions);

                            // 如果证件类型发生变化，根据证件类型自动填充相关字段（仅在字段为空时）
                            if (value.idType !== data.basic?.idType) {
                              console.log("证件类型发生变化，执行完整的字段填充");
                              const defaults = getDefaultsByIdType(value.idType);

                              // 1. 手机区号（仅在电话号码为空时）
                              if (defaults.phoneCode && !newData.personal.phone) {
                                newData.personal.phone = `${defaults.phoneCode} `;
                              }

                              // 2. 出生国家（仅在为空时）
                              if (defaults.countryCode && !newData.tax.countryOfBirth) {
                                newData.tax.countryOfBirth = defaults.countryCode;
                              }

                              // 3. 通讯地址国家（仅在为空时）
                              if (defaults.countryCode && !(newData.personal as any).country) {
                                (newData.personal as any).country = defaults.countryCode;
                              }

                              // 4. 国籍（仅在为空时）
                              if (defaults.countryCode && !newData.tax.nationality) {
                                newData.tax.nationality = defaults.countryCode;
                              }

                              // 5. 税务国家（仅在为空时）
                              if (defaults.countryCode && !(newData.tax as any).taxCountry) {
                                (newData.tax as any).taxCountry = defaults.countryCode;
                              }

                              // 同步更新表单字段值
                              form.setFieldsValue({
                                basic: {
                                  birthDate: newData.basic.birthDate,
                                  gender: newData.basic.gender,
                                  salutation: newData.personal.salutation, // 将 personal.salutation 同步到表单的 basic.salutation
                                },
                                personal: {
                                  phone: newData.personal.phone,
                                  addressData: {
                                    country: (newData.personal as any).country,
                                    address: (newData.personal as any).address,
                                  },
                                },
                                tax: {
                                  countryOfBirth: newData.tax.countryOfBirth,
                                  countryOfResidence: newData.tax.countryOfResidence,
                                  nationality: newData.tax.nationality,
                                  taxCountry: (newData.tax as any).taxCountry,
                                  hasHKIDCard: newData.tax.hasHKIDCard,
                                },
                              });
                              console.log("✅ 表单字段已同步更新（证件类型变化）");
                            } else {
                              console.log("证件类型未变化，仅同步 hasHKIDCard 和解析字段");
                              // 即使证件类型没有变化，也要同步更新 hasHKIDCard 和解析出的字段
                              form.setFieldsValue({
                                basic: {
                                  birthDate: newData.basic.birthDate,
                                  gender: newData.basic.gender,
                                  salutation: newData.personal.salutation, // 将 personal.salutation 同步到表单的 basic.salutation
                                },
                                tax: {
                                  hasHKIDCard: newData.tax.hasHKIDCard,
                                },
                              });
                              console.log("✅ 表单字段已同步更新（证件类型未变化）");
                            }

                            console.log("调用 onChange，传递 newData");
                            onChange(newData);
                            console.log("=== IdDocumentInput onChange 完成 ===\n");
                          }}
                          readOnly={readOnly}
                        />
                      </div>
                    </Form.Item>
                  </Col>
                )}
                {isVisible("basic.chineseSurname") && (
                  <Col span={12}>
                    <Form.Item
                      label="姓氏(中文)"
                      name={["basic", "chineseSurname"]}
                      rules={[{ required: isRequired("basic.chineseSurname") }, { pattern: /^[\u4e00-\u9fa5-]+$/, message: "只能輸入中文或-" }]}
                    >
                      <Input
                        placeholder="請輸入"
                        value={data.basic?.chineseSurname}
                        onChange={(e) => updateData("basic", "chineseSurname", e.target.value)}
                        onBlur={(e) => {
                          const filtered = e.target.value?.replace(/[^\u4e00-\u9fa5-]/g, "");
                          updateData("basic", "chineseSurname", filtered);
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                {isVisible("basic.chineseGivenName") && (
                  <Col span={12}>
                    <Form.Item
                      label="名字(中文)"
                      name={["basic", "chineseGivenName"]}
                      rules={[{ required: isRequired("basic.chineseGivenName") }, { pattern: /^[\u4e00-\u9fa5-]+$/, message: "只能輸入中文或-" }]}
                    >
                      <Input
                        placeholder="請輸入"
                        value={data.basic?.chineseGivenName}
                        onChange={(e) => updateData("basic", "chineseGivenName", e.target.value)}
                        onBlur={(e) => {
                          const filtered = e.target.value?.replace(/[^\u4e00-\u9fa5-]/g, "");
                          updateData("basic", "chineseGivenName", filtered);
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                {isVisible("basic.englishSurname") && (
                  <Col span={12}>
                    <Form.Item label="姓氏(英文)" name={["basic", "englishSurname"]} rules={[{ required: isRequired("basic.englishSurname") }]}>
                      <Input
                        inputMode="latin"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        autoComplete="off"
                        placeholder="請輸入"
                        value={data.basic?.englishSurname}
                        onChange={(e) => {
                          // 只允许英文字母和空格
                          const val = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                          updateData("basic", "englishSurname", val);
                        }}
                        onBlur={(e) => {
                          // 失焦时转换为大写
                          const val = e.target.value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
                          updateData("basic", "englishSurname", val);
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                {isVisible("basic.englishGivenName") && (
                  <Col span={12}>
                    <Form.Item label="名字(英文)" name={["basic", "englishGivenName"]} rules={[{ required: isRequired("basic.englishGivenName") }]}>
                      <Input
                        inputMode="latin"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        autoComplete="off"
                        placeholder="請輸入"
                        value={data.basic?.englishGivenName}
                        onChange={(e) => {
                          // 只允许英文字母和空格
                          const val = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                          updateData("basic", "englishGivenName", val);
                        }}
                        onBlur={(e) => {
                          // 失焦时转换为大写
                          const val = e.target.value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
                          updateData("basic", "englishGivenName", val);
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}

                {/* 性別和稱謂放在同一行，且性別和稱謂雙向聯動 */}
                {isVisible("basic.gender") && (
                  <Col span={12}>
                    <Form.Item label="性別" name={["basic", "gender"]} rules={[{ required: isRequired("basic.gender") }]}>
                      <GenderSelector disabled={readOnly} />
                    </Form.Item>
                  </Col>
                )}
                {isVisible("basic.salutation") && (
                  <Col span={12}>
                    <Form.Item label="稱謂" name={["basic", "salutation"]} rules={[{ required: isRequired("basic.salutation") }]}>
                      <SalutationSelector disabled={readOnly} />
                    </Form.Item>
                  </Col>
                )}
                {isVisible("basic.birthDate") && (
                  <Col span={12}>
                    <Form.Item label="出生日期" name={["basic", "birthDate"]} rules={[{ required: isRequired("basic.birthDate") }]}>
                      <BirthDateInput value={data.basic?.birthDate} onChange={(dateString) => updateData("basic", "birthDate", dateString)} readOnly={readOnly} />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </CustomCard>

            {/* Personal Info */}
            {!isSimplifiedForm && (
              <CustomCard title={`${getSectionNumber(2)} ${title}個人資料`} className="form-card mb-4" bordered={false}>
                <Row gutter={24}>
                  {isVisible("personal.phone") && (
                    <Col span={12}>
                      <Form.Item
                        className={styles.fullWidthLabel}
                        label={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ flex: 1 }}>手提電話</span>
                            {canUsePolicyholderQuickFill && (
                              <Space size={12}>
                                <span style={quickActionStyle} onClick={handleCopyPolicyholderPhone}>
                                  同投保人手提電話
                                </span>
                              </Space>
                            )}
                          </div>
                        }
                        name={["personal", "phone"]}
                        rules={[{ required: isRequired("personal.phone") }]}
                      >
                        <PhoneInput value={data.personal?.phone} onChange={(phone) => updateData("personal", "phone", phone)} readOnly={readOnly} />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.email") && (
                    <Col span={12}>
                      <Form.Item
                        className={styles.fullWidthLabel}
                        label={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ flex: 1 }}>電郵</span>
                            {canUsePolicyholderQuickFill && (
                              <Space size={12}>
                                <span style={quickActionStyle} onClick={handleCopyPolicyholderEmail}>
                                  同投保人電郵
                                </span>
                              </Space>
                            )}
                          </div>
                        }
                        name={["personal", "email"]}
                        rules={[
                          {
                            required: isRequired("personal.email"),
                            message: "请输入电邮",
                          },
                        ]}
                      >
                        <Input placeholder="請輸入" value={data.personal?.email} onChange={(e) => updateData("personal", "email", e.target.value)} />
                      </Form.Item>
                    </Col>
                  )}
                  {!isInsured && !isSecondInsured && !isSecondPolicyholder && data.basic?.idType === IdType.IDCard && isVisible("personal.residentialAddress") && (
                    <Col span={24}>
                      <Form.Item label="住宅(身份證)地址" name={["personal", "residentialAddress"]} rules={[{ required: isRequired("personal.residentialAddress") }]}>
                        <Space.Compact style={{ width: "100%" }}>
                          <Input placeholder="請輸入詳細住宅(身份證)地址" value={(data.personal as any)?.residentialAddress} onChange={(e) => updateData("personal", "residentialAddress", e.target.value)} />
                        </Space.Compact>
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.country") && isVisible("personal.address") && (
                    <Col span={24}>
                      <Form.Item
                        className={styles.fullWidthLabel}
                        label={
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ flex: 1 }}>通訊地址</span>
                            {/* Only show "Same as above" if residential address is visible (which is only for primary Policyholder with China ID) */}
                            {!isInsured && !isSecondInsured && !isSecondPolicyholder && data.basic?.idType === IdType.IDCard && (
                              <span
                                style={quickActionStyle}
                                onClick={() => {
                                  // Copy Residential to Communication
                                  const residentialDetail = (data.personal as any)?.residentialAddress;
                                  if (residentialDetail) {
                                    const newData = { ...data };
                                    if (!newData.personal) newData.personal = {};
                                    (newData.personal as any).address = residentialDetail;

                                    // Update form field value to trigger re-render
                                    form.setFieldsValue({
                                      personal: {
                                        ...newData.personal,
                                        addressData: {
                                          country: (newData.personal as any)?.country,
                                          address: residentialDetail,
                                        },
                                      },
                                    });

                                    onChange(newData);
                                  } else {
                                    message.warning("住宅(身份證)地址為空，無法複製");
                                  }
                                }}
                              >
                                同上
                              </span>
                            )}
                            {canUsePolicyholderQuickFill && (
                              <Space size={12} wrap>
                                <span style={quickActionStyle} onClick={handleCopyPolicyholderCommunicationAddress}>
                                  同投保人通訊地址
                                </span>
                                <span style={quickActionStyle} onClick={handleCopyPolicyholderResidentialAddress}>
                                  同投保人住宅(身份證)地址
                                </span>
                              </Space>
                            )}
                          </div>
                        }
                        name={["personal", "addressData"]}
                        required={isRequired("personal.country") || isRequired("personal.address")}
                        rules={[
                          {
                            validator: async (_, value) => {
                              const countryReq = isRequired("personal.country");
                              const addressReq = isRequired("personal.address");

                              if (!value) {
                                if (countryReq || addressReq) return Promise.reject(new Error("請輸入通訊地址"));
                                return Promise.resolve();
                              }

                              if (countryReq && !value.country) {
                                return Promise.reject(new Error("請選擇國家/地區"));
                              }
                              if (addressReq && !value.address) {
                                return Promise.reject(new Error("請輸入詳細地址"));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AddressInput
                          value={{
                            country: (data.personal as any)?.country,
                            address: (data.personal as any)?.address,
                          }}
                          onChange={(value) => {
                            const newData = { ...data };
                            if (!newData.personal) newData.personal = {};
                            (newData.personal as any).country = value.country;
                            (newData.personal as any).address = value.address;
                            onChange(newData);
                          }}
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.height") && (
                    <Col span={12}>
                      <Form.Item label="身高(CM)" name={["personal", "height"]} rules={[{ required: isRequired("personal.height") }]}>
                        <AmountInput style={{ width: "100%" }} placeholder="請輸入" value={data.personal?.height} onChange={(v) => updateData("personal", "height", v)} suffix="CM" />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.weight") && (
                    <Col span={12}>
                      <Form.Item label="體重(KG)" name={["personal", "weight"]} rules={[{ required: isRequired("personal.weight") }]}>
                        <AmountInput style={{ width: "100%" }} placeholder="請輸入" value={data.personal?.weight} onChange={(v) => updateData("personal", "weight", v)} suffix="KG" />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.education") && (
                    <Col span={12}>
                      <Form.Item label="教育程度" name={["personal", "education"]} rules={[{ required: isRequired("personal.education") }]}>
                        <SelectInput
                          options={educationOptions}
                          value={(data.personal as any)?.education}
                          onChange={(v) => updateData("personal", "education", v)}
                          title="教育程度"
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.marriage") && (
                    <Col span={12}>
                      <Form.Item label="婚姻狀況" name={["personal", "marriage"]} rules={[{ required: isRequired("personal.marriage") }]}>
                        <SelectInput options={marriageOptions} value={(data.personal as any)?.marriage} onChange={(v) => updateData("personal", "marriage", v)} title="婚姻狀況" readOnly={readOnly} />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("personal.smoke") && (
                    <Col span={12}>
                      <Form.Item label="是否吸煙" name={["personal", "smoke"]} rules={[{ required: isRequired("personal.smoke") }]}>
                        <TagSelector options={smokeOptions} value={data.personal?.smoke} onChange={(v) => updateData("personal", "smoke", v)} disabled={readOnly} allowClear={false} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </CustomCard>
            )}

            {/* Employment Info */}
            {!isSimplifiedForm && (
              <CustomCard title={`${getSectionNumber(3)} ${title}工作資料`} className="form-card mb-4" bordered={false}>
                <Row gutter={24}>
                  {isVisible("work.type") && (
                    <Col span={12}>
                      <Form.Item label="就業類型" name={["work", "type"]} rules={[{ required: isRequired("work.type") }]}>
                        <SelectInput
                          options={jobTypeOptions}
                          value={(data as PolicyholderVO).work?.type}
                          onChange={(v) => {
                            const currentWork = (data as PolicyholderVO).work || {};
                            let newWork: any = { ...currentWork, type: v };
                            if (v === "UNPAID_PERSON") {
                              newWork = {
                                ...newWork,
                                jobTitle: undefined,
                                employer: {
                                  name: undefined,
                                  address: undefined,
                                  industry: undefined,
                                },
                              };
                              form.setFieldsValue({
                                work: {
                                  jobTitle: undefined,
                                  employer: { name: undefined, address: undefined, industry: undefined },
                                },
                              });
                            }
                            onChange({ ...data, work: newWork } as any);
                          }}
                          title="就業類型"
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("work.jobTitle") && (
                    <Col span={12}>
                      <Form.Item label="職位及日常職務" name={["work", "jobTitle"]} rules={[{ required: isRequired("work.jobTitle") }]}>
                        <Input placeholder="請輸入" value={(data as PolicyholderVO).work?.jobTitle} onChange={(e) => updateData("work", "jobTitle", e.target.value)} />
                      </Form.Item>
                    </Col>
                  )}
                  {/* 投保人显示完整工作资料 */}
                  {!isPrimaryInsured && isVisible("work.employer.name") && (
                    <Col span={12}>
                      <Form.Item label="僱主(公司)名稱" name={["work", "employer", "name"]} rules={[{ required: isRequired("work.employer.name") }]}>
                        <Input
                          placeholder="請輸入"
                          value={(data as PolicyholderVO).work?.employer?.name}
                          onChange={(e) => {
                            const currentWork = (data as PolicyholderVO).work || {};
                            const currentEmployer = currentWork.employer || {};
                            const newWork = {
                              ...currentWork,
                              employer: {
                                ...currentEmployer,
                                name: e.target.value,
                              },
                            };
                            onChange({ ...data, work: newWork } as any);
                          }}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {!isPrimaryInsured && isVisible("work.employer.address") && (
                    <Col span={12}>
                      <Form.Item label="僱主(公司)地址" name={["work", "employer", "address"]} rules={[{ required: isRequired("work.employer.address") }]}>
                        <Input
                          placeholder="請輸入"
                          value={(data as PolicyholderVO).work?.employer?.address}
                          onChange={(e) =>
                            updateData("work", "employer", {
                              ...(data as PolicyholderVO).work?.employer,
                              address: e.target.value,
                            })
                          }
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {!isPrimaryInsured && isVisible("work.employer.industry") && (
                    <Col span={12}>
                      <Form.Item label="業務性質(行業)" name={["work", "employer", "industry"]} rules={[{ required: isRequired("work.employer.industry") }]}>
                        <SelectInput
                          options={industryOptions}
                          value={(data as PolicyholderVO).work?.employer?.industry}
                          onChange={(v) => {
                            const currentWork = (data as PolicyholderVO).work || {};
                            const currentEmployer = currentWork.employer || {};
                            const newWork = {
                              ...currentWork,
                              employer: {
                                ...currentEmployer,
                                industry: v,
                              },
                            };
                            onChange({ ...data, work: newWork } as any);
                          }}
                          title="業務性質(行業)"
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {!isPrimaryInsured && isVisible("work.intendedRetirementAge") && (
                    <Col span={12}>
                      <Form.Item label="預計退休年齡" name={["work", "intendedRetirementAge"]} rules={[{ required: isRequired("work.intendedRetirementAge") }]}>
                        <SelectInput
                          options={retirementAgeOptions}
                          value={(data as PolicyholderVO).work?.intendedRetirementAge}
                          onChange={(v) => {
                            const currentWork = (data as PolicyholderVO).work || {};
                            let newWork: any = { ...currentWork, intendedRetirementAge: v };
                            if (v !== "OTHERS") {
                              newWork = { ...newWork, intendedRetirementAgeOther: undefined };
                              form.setFieldsValue({
                                work: { intendedRetirementAgeOther: undefined },
                              });
                            }
                            onChange({ ...data, work: newWork } as any);
                          }}
                          title="預計退休年齡"
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {!isPrimaryInsured && isVisible("work.intendedRetirementAgeOther") && (
                    <Col span={12}>
                      <Form.Item label="預計退休年齡其他說明" name={["work", "intendedRetirementAgeOther"]} rules={[{ required: isRequired("work.intendedRetirementAgeOther") }]}>
                        <Input
                          placeholder="請輸入"
                          value={(data as PolicyholderVO).work?.intendedRetirementAgeOther}
                          onChange={(e) => updateData("work", "intendedRetirementAgeOther", e.target.value)}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </CustomCard>
            )}

            {/* Nationality & Tax */}
            {!isSimplifiedForm && (
              <CustomCard title={`${getSectionNumber(4)} ${title}國籍、稅務等資料`} className="form-card mb-4" bordered={false}>
                <Row gutter={24}>
                  {isVisible("tax.nationality") && (
                    <Col span={12}>
                      <Form.Item label="國籍(國家/地區)" name={["tax", "nationality"]} rules={[{ required: isRequired("tax.nationality") }]}>
                        <CountrySelect type="country" value={data.tax?.nationality} onChange={(_, c) => updateData("tax", "nationality", c?.countryCode)} readOnly={readOnly} placeholder="請選擇" />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.countryOfBirth") && (
                    <Col span={12}>
                      <Form.Item label="出生國家" name={["tax", "countryOfBirth"]} rules={[{ required: isRequired("tax.countryOfBirth") }]}>
                        <CountrySelect
                          type="country"
                          value={data.tax?.countryOfBirth}
                          onChange={(_, c) => updateData("tax", "countryOfBirth", c?.countryCode)}
                          readOnly={readOnly}
                          placeholder="請選擇"
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isPolicyholder && isZuuTenant && (
                    <Col span={12}>
                      <Form.Item label="居住國家" name={["tax", "countryOfResidence"]} rules={[{ required: false }]}>
                        <CountrySelect
                          type="country"
                          value={data.tax?.countryOfResidence}
                          onChange={(_, c) => updateData("tax", "countryOfResidence", c?.countryCode)}
                          readOnly={readOnly}
                          placeholder="請選擇"
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.hkEntryIdType") && (
                    <Col span={12}>
                      <Form.Item label="入境香港證件類型" name={["tax", "hkEntryIdType"]} rules={[{ required: isRequired("tax.hkEntryIdType") }]}>
                        <SelectInput
                          options={hkEntryIdTypeOptions}
                          value={data.tax?.hkEntryIdType}
                          onChange={(v) => updateData("tax", "hkEntryIdType", v)}
                          title="入境香港證件類型"
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.hkEntryIdNumber") && (
                    <Col span={12}>
                      <Form.Item label="入境香港證件號碼" name={["tax", "hkEntryIdNumber"]} rules={[{ required: isRequired("tax.hkEntryIdNumber") }]}>
                        <Input placeholder="請輸入" value={data.tax?.hkEntryIdNumber} onChange={(e) => updateData("tax", "hkEntryIdNumber", e.target.value)} />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.hasOtherTaxResidency") && (
                    <Col span={12}>
                      <Form.Item label="是否有其他稅務居民身份" name={["tax", "hasOtherTaxResidency"]} rules={[{ required: isRequired("tax.hasOtherTaxResidency") }]}>
                        <TagSelector
                          options={whetherOptions}
                          value={data.tax?.hasOtherTaxResidency}
                          onChange={(v) => updateData("tax", "hasOtherTaxResidency", v)}
                          disabled={readOnly}
                          allowClear={false}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {/* Conditional Tax Fields - Only show when hasOtherTaxResidency is true */}
                  {isVisible("tax.hasOtherTaxResidency") && data.tax?.hasOtherTaxResidency && isVisible("tax.taxCountry") && (
                    <Col span={12}>
                      <Form.Item label="稅務國家" name={["tax", "taxCountry"]} rules={[{ required: isRequired("tax.taxCountry") }]}>
                        <CountrySelect type="country" value={data.tax?.taxCountry} onChange={(_, c) => updateData("tax", "taxCountry", c?.countryCode)} readOnly={readOnly} placeholder="請選擇" />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.hasOtherTaxResidency") && data.tax?.hasOtherTaxResidency && isVisible("tax.taxCode") && (
                    <Col span={12}>
                      <Form.Item label="稅務編號" name={["tax", "taxCode"]} rules={[{ required: isRequired("tax.taxCode") }]}>
                        <Input placeholder="請輸入" value={data.tax?.taxCode} onChange={(e) => updateData("tax", "taxCode", e.target.value)} />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.isUSCitizen") && (
                    <Col span={12}>
                      <Form.Item label="是否擁有美國綠卡身份" name={["tax", "isUSCitizen"]} rules={[{ required: isRequired("tax.isUSCitizen") }]}>
                        <TagSelector options={whetherOptions} value={data.tax?.isUSCitizen} onChange={(v) => updateData("tax", "isUSCitizen", v)} disabled={readOnly} allowClear={false} />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.hasHKIDCard") && (
                    <Col span={12}>
                      <Form.Item label="是否持有香港身份證" name={["tax", "hasHKIDCard"]} rules={[{ required: isRequired("tax.hasHKIDCard") }]}>
                        <TagSelector options={whetherOptions} value={data.tax?.hasHKIDCard} onChange={(v) => updateData("tax", "hasHKIDCard", v)} disabled={readOnly} allowClear={false} />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("tax.isHKPermanentResident") && (
                    <Col span={12}>
                      <Form.Item label="是否香港永久居民" name={["tax", "isHKPermanentResident"]} rules={[{ required: isRequired("tax.isHKPermanentResident") }]}>
                        <TagSelector
                          options={whetherOptions}
                          value={data.tax?.isHKPermanentResident}
                          onChange={(v) => updateData("tax", "isHKPermanentResident", v)}
                          disabled={readOnly}
                          allowClear={false}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </CustomCard>
            )}

            {/* Family Members */}
            {!isSimplifiedForm && !isPrimaryInsured && (
              <CustomCard
                title={`${getSectionNumber(5)} ${resolvedFamilyMembersSectionTitle}`}
                className="form-card mb-4"
                bordered={false}
                extra={
                  <CustomButton
                    bgColor="var(--theme-primary)"
                    color="#FFFFFF"
                    width={120}
                    height={32}
                    onClick={() => {
                      const newMembers = [...((data as PolicyholderVO).familyMembers || [])];
                      newMembers.push({
                        relationship: null,
                        age: null,
                        isDependent: null,
                      });
                      onChange({ ...data, familyMembers: newMembers } as any);
                    }}
                  >
                    新增成員
                  </CustomButton>
                }
              >
                {((data as PolicyholderVO).familyMembers || []).map((member, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: 24,
                      padding: 16,
                      background: "#FAFAFA",
                      borderRadius: 8,
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 16,
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>成員 {index + 1}</span>
                      <CustomButton
                        bgColor="transparent"
                        color="#ff4d4f"
                        icon={<MinusCircleOutlined />}
                        width="auto"
                        onClick={() => {
                          const newMembers = [...((data as PolicyholderVO).familyMembers || [])];
                          newMembers.splice(index, 1);
                          onChange({
                            ...data,
                            familyMembers: newMembers,
                          } as any);
                        }}
                      >
                        刪除
                      </CustomButton>
                    </div>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item label="成員年齡">
                          <AmountInput
                            placeholder="請輸入"
                            style={{ width: "100%" }}
                            value={member.age}
                            onChange={(v) => {
                              const newMembers = [...((data as PolicyholderVO).familyMembers || [])];
                              newMembers[index] = {
                                ...newMembers[index],
                                age: v ?? undefined,
                              };
                              onChange({
                                ...data,
                                familyMembers: newMembers,
                              } as any);
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="成員關係">
                          <SelectInput
                            options={familyRelationshipOptions.map((option) => {
                              // 除了子女关系，其他关系不能重复选择
                              const isChild = option.value === FamilyMemberRelationship.Child;
                              const isCurrentSelected = member.relationship === option.value;
                              const isAlreadySelected = !isChild && ((data as PolicyholderVO).familyMembers || []).some((m, i) => i !== index && m.relationship === option.value);

                              return {
                                ...option,
                                disabled: isAlreadySelected && !isCurrentSelected,
                              };
                            })}
                            value={member.relationship}
                            onChange={(v) => {
                              const newMembers = [...((data as PolicyholderVO).familyMembers || [])];
                              newMembers[index] = {
                                ...newMembers[index],
                                relationship: v,
                              };
                              onChange({
                                ...data,
                                familyMembers: newMembers,
                              } as any);
                            }}
                            title="成員關係"
                            readOnly={readOnly}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="是否受供養">
                          <TagSelector
                            options={whetherOptions}
                            value={member.isDependent}
                            onChange={(v) => {
                              const newMembers = [...((data as PolicyholderVO).familyMembers || [])];
                              newMembers[index] = {
                                ...newMembers[index],
                                isDependent: v,
                              };
                              onChange({
                                ...data,
                                familyMembers: newMembers,
                              } as any);
                            }}
                            disabled={readOnly}
                            allowClear={false}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                ))}
              </CustomCard>
            )}

            {/* 06 Other Info */}
            {!isSimplifiedForm && !isPrimaryInsured && (
              <CustomCard title="06 其他資料" className="form-card mb-4" bordered={false}>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label="是否專業投資者/PI"
                      style={{ width: "100%" }}
                      labelAlign="right"
                      name={["isProfessionalInvestor"]}
                      rules={[{ required: isRequired("other.isProfessionalInvestor") }]}
                    >
                      <TagSelector
                        options={otherBooleanOptions}
                        value={getOtherBooleanValue("isProfessionalInvestor")}
                        onChange={(value) => updateData("other", "isProfessionalInvestor", value)}
                        disabled={readOnly}
                        allowClear={false}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="是否重點關注客戶"
                      style={{ width: "100%" }}
                      labelAlign="right"
                      name={["isVulnerableCustomer"]}
                      rules={[{ required: isRequired("other.isVulnerableCustomer") }]}
                    >
                      <div className={styles.vulnerableCustomerField}>
                        <div className={styles.vulnerableCustomerNote}>
                          {vulnerableCustomerNote}
                        </div>
                        <TagSelector
                          options={otherBooleanOptions}
                          value={getOtherBooleanValue("isVulnerableCustomer")}
                          onChange={(value) => updateData("other", "isVulnerableCustomer", value)}
                          disabled={readOnly}
                          allowClear={false}
                        />
                      </div>
                    </Form.Item>
                  </Col>
                  {isVisible("other.witnessName") && (
                    <Col span={12}>
                      <Form.Item
                        label="見證人姓名"
                        name={["witnessName"]}
                        rules={[
                          {
                            required: isRequired("other.witnessName"),
                            message: "請輸入見證人姓名",
                          },
                        ]}
                      >
                        <Input placeholder="請輸入" value={data.other?.witnessName} onChange={(e) => updateData("other", "witnessName", e.target.value)} />
                      </Form.Item>
                    </Col>
                  )}
                  {(isVisible("other.witnessIdType") || isVisible("other.witnessIdNumber")) && (
                    <Col span={12}>
                      <Form.Item
                        label="見證人證件"
                        name={["witnessIdDocument"]}
                        rules={[
                          {
                            required: isRequired("other.witnessIdType") || isRequired("other.witnessIdNumber"),
                            validator: async (_, value) => {
                              const idTypeRequired = isRequired("other.witnessIdType");
                              const idNumberRequired = isRequired("other.witnessIdNumber");

                              if (idTypeRequired || idNumberRequired) {
                                if (!value) {
                                  return Promise.reject(new Error("請完善見證人證件資料"));
                                }
                                if (idTypeRequired && !value.idType) {
                                  return Promise.reject(new Error("請選擇見證人證件類型"));
                                }
                                if (idNumberRequired && !value.idNumber) {
                                  return Promise.reject(new Error("請輸入見證人證件號碼"));
                                }
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <IdDocumentInput
                          value={{
                            idType: data.other?.witnessIdType,
                            idNumber: data.other?.witnessIdNumber,
                          }}
                          onChange={(value: IdDocumentValue) => {
                            const newData = { ...data };
                            if (!newData.other) newData.other = {};
                            newData.other.witnessIdType = value.idType;
                            newData.other.witnessIdNumber = value.idNumber;
                            onChange(newData);
                          }}
                          readOnly={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {isVisible("other.remark") && (
                    <Col span={24}>
                      <Form.Item label="備註/其他說明" name={["remark"]} rules={[{ required: isRequired("other.remark") }]}>
                        <Input placeholder="請輸入" value={data.other?.remark ?? data.remark} onChange={(e) => updateData("other", "remark", e.target.value)} />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </CustomCard>
            )}
          </>
        )}
      </Form>
    </div>
  );
});
